import React, { useState } from "react";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import { Loader2, Check, Download } from "lucide-react";

const industries = [
  "Creative / Design",
  "Wellness & Health",
  "Professional Services",
  "Coaching & Education",
  "Home Services",
  "Retail / E-commerce",
  "Food & Hospitality",
  "Nonprofit",
  "Technology",
  "Other",
];

export const REPORTS = [
  {
    id: "beauty_report",
    label: "Beauty Industry \u2014 Opportunity Brief",
    file: "/brand/BOOMM_Beauty_Report.pdf",
    blurb: "A signal-led look at consumer preference shifts in beauty.",
  },
  {
    id: "spa_report",
    label: "Spa Industry \u2014 Opportunity Brief",
    file: "/brand/BOOMM_Spa_Report.pdf",
    blurb: "Where wellness and spa demand is moving next.",
  },
  {
    id: "beauty_infographic",
    label: "Beauty \u2014 Infographic Summary",
    file: "/brand/BOOMM_Beauty_Infographic.pdf",
    blurb: "A one-page visual snapshot of the beauty brief.",
  },
  {
    id: "spa_infographic",
    label: "Spa \u2014 Infographic Summary",
    file: "/brand/BOOMM_Spa_Infographic.pdf",
    blurb: "A one-page visual snapshot of the spa brief.",
  },
];

const initial = {
  first_name: "",
  email: "",
  business_name: "",
  website: "",
  industry: "",
  challenge: "",
  report: "",
  consent: false,
};

const validate = (v) => {
  const e = {};
  if (!v.first_name.trim()) e.first_name = "Required";
  if (!/^\S+@\S+\.\S+$/.test(v.email)) e.email = "Enter a valid email";
  if (!v.business_name.trim()) e.business_name = "Required";
  if (!v.website.trim()) e.website = "Required";
  if (!v.industry) e.industry = "Select an industry";
  if (!v.report) e.report = "Choose a sample brief";
  if (!v.challenge.trim() || v.challenge.trim().length < 6)
    e.challenge = "Tell us a little more";
  if (!v.consent) e.consent = "Please consent to continue";
  return e;
};

export const LeadForm = () => {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submittedReport, setSubmittedReport] = useState(null);

  const set = (k) => (e) => {
    const val = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((s) => ({ ...s, [k]: val }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    try {
      setLoading(true);
      await submitLead(values);
      const r = REPORTS.find((x) => x.id === values.report);
      setSubmittedReport(r);
      setSuccess(true);
      toast.success("Your sample brief is on its way.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong submitting your request. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        className="card-edge p-10 md:p-12 rounded-[2px]"
        data-testid="lead-form-success"
      >
        <div className="w-12 h-12 rounded-full border border-[#B58B42] inline-flex items-center justify-center text-[#B58B42]">
          <Check size={22} />
        </div>
        <h3 className="font-display text-2xl md:text-3xl mt-6 text-[#2A2422]">
          Your sample brief is on its way.
        </h3>
        <p className="font-body text-[#50616B] mt-4 leading-relaxed">
          BOOMM helps turn fragmented signals into clearer next steps. Look for an
          email from{" "}
          <span className="text-[#2A2422] font-medium">marjorie@boommgroup.com</span>{" "}
          shortly.
        </p>
        {submittedReport && (
          <div className="mt-8 bg-[#FBF9F4] border border-[#2A2422]/10 rounded-[2px] p-5 flex items-start justify-between gap-4">
            <div>
              <p className="eyebrow text-[#B58B42]">Your selection</p>
              <p className="mt-1 font-display text-[#2A2422] text-lg">
                {submittedReport.label}
              </p>
            </div>
            <a
              href={submittedReport.file}
              download
              data-testid="lead-success-download"
              className="btn-primary px-5 py-3 text-xs tracking-wide rounded-[2px] inline-flex items-center gap-2 whitespace-nowrap"
            >
              <Download size={14} />
              Download PDF
            </a>
          </div>
        )}
        <button
          className="mt-8 btn-secondary px-6 py-3 text-sm tracking-wide rounded-[2px]"
          onClick={() => {
            setValues(initial);
            setSubmittedReport(null);
            setSuccess(false);
          }}
          data-testid="lead-form-reset"
        >
          Send another request
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="card-edge p-8 md:p-10 rounded-[2px]"
      data-testid="lead-form"
      noValidate
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="field-label" htmlFor="first_name">First Name</label>
          <input
            id="first_name"
            data-testid="lead-first-name"
            className="field-input"
            type="text"
            value={values.first_name}
            onChange={set("first_name")}
            placeholder="Marjorie"
          />
          {errors.first_name && (
            <p className="mt-1 text-xs text-[#9C3B2A]">{errors.first_name}</p>
          )}
        </div>
        <div>
          <label className="field-label" htmlFor="email">Email Address</label>
          <input
            id="email"
            data-testid="lead-email"
            className="field-input"
            type="email"
            value={values.email}
            onChange={set("email")}
            placeholder="you@business.com"
          />
          {errors.email && (
            <p className="mt-1 text-xs text-[#9C3B2A]">{errors.email}</p>
          )}
        </div>
        <div>
          <label className="field-label" htmlFor="business_name">Business Name</label>
          <input
            id="business_name"
            data-testid="lead-business-name"
            className="field-input"
            type="text"
            value={values.business_name}
            onChange={set("business_name")}
            placeholder="Studio, agency, or LLC"
          />
          {errors.business_name && (
            <p className="mt-1 text-xs text-[#9C3B2A]">{errors.business_name}</p>
          )}
        </div>
        <div>
          <label className="field-label" htmlFor="website">Website URL</label>
          <input
            id="website"
            data-testid="lead-website"
            className="field-input"
            type="text"
            value={values.website}
            onChange={set("website")}
            placeholder="https://"
          />
          {errors.website && (
            <p className="mt-1 text-xs text-[#9C3B2A]">{errors.website}</p>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="field-label" htmlFor="industry">Industry</label>
          <select
            id="industry"
            data-testid="lead-industry"
            className="field-input"
            value={values.industry}
            onChange={set("industry")}
          >
            <option value="">Select an industry</option>
            {industries.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
          {errors.industry && (
            <p className="mt-1 text-xs text-[#9C3B2A]">{errors.industry}</p>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="field-label" htmlFor="report">
            Which Sample Opportunity Brief?
          </label>
          <select
            id="report"
            data-testid="lead-report"
            className="field-input"
            value={values.report}
            onChange={set("report")}
          >
            <option value="">Select a sample brief</option>
            {REPORTS.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
          {errors.report && (
            <p className="mt-1 text-xs text-[#9C3B2A]">{errors.report}</p>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="field-label" htmlFor="challenge">
            Biggest Current Business Challenge
          </label>
          <textarea
            id="challenge"
            data-testid="lead-challenge"
            className="field-input"
            value={values.challenge}
            onChange={set("challenge")}
            placeholder="Where does your business feel unclear, overloaded, or stuck?"
          />
          {errors.challenge && (
            <p className="mt-1 text-xs text-[#9C3B2A]">{errors.challenge}</p>
          )}
        </div>
      </div>

      <label
        htmlFor="consent"
        className="mt-6 flex items-start gap-3 cursor-pointer select-none"
      >
        <input
          id="consent"
          data-testid="lead-consent"
          type="checkbox"
          checked={values.consent}
          onChange={set("consent")}
          className="mt-1 accent-[#B58B42] w-4 h-4"
        />
        <span className="font-body text-sm text-[#50616B] leading-relaxed">
          I&apos;d like to receive the sample brief and occasional intelligence
          notes from BOOMM Group. No spam &mdash; unsubscribe any time.
        </span>
      </label>
      {errors.consent && (
        <p className="mt-1 text-xs text-[#9C3B2A]">{errors.consent}</p>
      )}

      <button
        type="submit"
        disabled={loading}
        data-testid="lead-submit"
        className="mt-8 btn-primary px-7 py-3.5 text-sm tracking-wide rounded-[2px] inline-flex items-center gap-2 disabled:opacity-70"
      >
        {loading && <Loader2 size={16} className="animate-spin" />}
        Send Me the Sample Brief
      </button>
    </form>
  );
};

export default LeadForm;
